/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import API from '@apps/nnc/services/actionplan';
import ActionPlanAPI from '@apps/nnc/services/actionplan';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { ActionPlanSchema, ActionPlanType } from '@apps/nnc/schemas/actionplan';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  Tooltip,
  alpha,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { PageTitle, Spinner } from '@/components/atoms';
import { enqueueSnackbar } from 'notistack';
import {
  DataGrid,
  GridActionsCellItem,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker } from '@mui/x-date-pickers';
import EditIcon from '@mui/icons-material/Edit';
import { FilePicker } from '@/components/atoms/filepicker';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadFileFromPath } from '@/utils/download';
import { formatDateSafely } from '@/utils/dateUtils';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FunctionalAreaAPI from '@apps/nnc/services/functionalarea';
import { useAuth } from '@/contexts/auth';

const formatBody = (str: string | undefined): number[] => {
  if (!str) return [];
  return str.includes('-')
    ? str.split('-').map((i) => Number(i))
    : [Number(str)];
};

const emptyAction = {
  description: '',
  status: undefined,
  due_date: undefined,
  area: '',
  responsible: '',
  implemented: undefined,
  displayed: false,
  functional_area: undefined,
};

const NNCAnalysisForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const ids = formatBody(id);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { area_leader } = useAuth();
  const [newAction, setNewAction] = useState<any>({
    description: '',
    status: undefined,
    due_date: undefined,
    area: '',
    responsible: '',
    implemented: undefined,
    displayed: false,
    functional_area: undefined,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [functionalAreas, setFunctionalAreas] = useState<any[]>([]);

  const columns = [
    {
      field: 'division',
      headerName: t('Identified Area'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_divisions || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_division || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'machine',
      headerName: t('NNC Machine'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_machine || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_machine || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'operator',
      headerName: t('Operator Generator'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.other_operators || [];
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {params.row.main_operator || ''}
            {machines.length > 0 && (
              <Tooltip
                title={
                  <>
                    {machines.map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  +{machines.length || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },

    {
      field: 'origin',
      headerName: t('Origin'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.origin?.label;
      },
      flex: 1,
    },
    {
      field: 'cause',
      headerName: t('Cause'),
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.cause?.label;
      },
      flex: 1,
    },
    {
      field: 'comment',
      headerName: t('Comment'),
      flex: 2,
    },
    {
      field: 'actions',
      width: 50,
      headerName: '',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <GridActionsCellItem
            sx={{
              background: '#2D354A',
              color: '#0093ff',
              borderRadius: '16px',
            }}
            key={`edit-${params?.row.id}`}
            icon={<DeleteOutlineIcon />}
            onClick={() => {}}
            label="remove"
          />
        );
      },
    },
  ];

  const { data, isLoading, error } = useQuery<{ data: ActionPlanType }, Error>({
    queryKey: ['entries', ids],
    queryFn: () => API.detail(id as string),
    enabled: ids.length > 0,
  });

  const form = useForm<ActionPlanType>({
    resolver: zodResolver(ActionPlanSchema),
    defaultValues: {
      comment_general: '',
      actions: [],
    },
  });

  const actions = form.watch('actions');

  const [failures, setFailures] = useState<any>([]);

  const { fields, prepend, remove } = useFieldArray({
    control: form.control,
    name: 'actions',
  });

  const handleAddCause = () => {
    setShowAddForm(true);
    setNewAction({
      ...emptyAction,
      status: { label: t('On Going'), id: 2 },
    });
  };

  const isFormValid = () => {
    const needsImplemented =
      newAction.status?.id === 3 &&
      newAction.functional_area?.id &&
      area_leader?.includes(newAction.functional_area.id);

    return (
      newAction.description?.trim() &&
      newAction.status &&
      newAction.due_date &&
      newAction.functional_area &&
      newAction.responsible?.trim() &&
      (!needsImplemented || newAction.implemented)
    );
  };

  const handleSaveNewAction = () => {
    const actionToSave = {
      ...newAction,
      displayed: true,
      due_date: newAction.due_date
        ? dayjs(newAction.due_date).toDate()
        : undefined,
    };

    if (editIndex !== null) {
      form.setValue(`actions.${editIndex}`, {
        ...actionToSave,
        id: newAction.actionId,
      });

      setEditIndex(null);
    } else {
      prepend(actionToSave);
    }
    setShowAddForm(false);
    setNewAction(emptyAction);
  };

  // const handleAddCause = () => {
  //   prepend({
  //     description: undefined,
  //     status: undefined,
  //     due_date: undefined,
  //     area: undefined,
  //     responsible: undefined,
  //     implemented: undefined,
  //     displayed: false,
  //   });
  // };

  const handleSubmit = async (data: ActionPlanType) => {
    try {
      setLoading(true);
      const actionsFormatted =
        data?.actions?.map((action) => ({
          ...action,
          due_date: action.due_date
            ? dayjs(action.due_date).format('YYYY-MM-DD')
            : undefined,
        })) || [];

      await ActionPlanAPI.update_action_plan(id as string, {
        failure_ids: ids,
        ...data,
        actions: actionsFormatted,
      });

      enqueueSnackbar(t('Action Plan created successfully!'), {
        variant: 'success',
      });
      navigate('/nnc/actionplan');
    } catch (e) {
      console.error(e);
      setLoading(false);
      enqueueSnackbar(t('Error when trying to create Action Plan'), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0) {
      console.log('Zod validation errors:', form.formState.errors);
      console.log('ZOD DATA: ', form.getValues());
    }
  }, [form.formState.errors, form]);

  useEffect(() => {
    const values = data?.data;
    const actionsFormated =
      values?.actions?.map((action) => ({
        ...action,
        due_date: action.due_date
          ? dayjs(action.due_date, 'DD/MM/YYYY').toDate()
          : undefined,
        implemented: action.implemented
          ? { id: 'true', label: t('Yes') }
          : { id: 'false', label: t('No') },
        displayed: true,
        actionId: action.id,
      })) || undefined;

    form.reset({
      comment_general: values?.comment_general || '',
      theme: values?.theme || '',
      actions: actionsFormated || [],
    });
    setFailures(values?.failure_mode || []);
  }, [data, form, t]);

  useEffect(() => {
    const getFunctionalAreas = async () => {
      try {
        const response = await FunctionalAreaAPI.select({});
        setFunctionalAreas(response?.data as any);
      } catch (error) {
        console.error('Error fetching functional areas:', error);
        enqueueSnackbar(t('Error fetching functional areas'), {
          variant: 'error',
        });
      }
    };

    getFunctionalAreas();
  }, [t]);

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Erro ao carregar dados: {(error as Error).message}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <PageTitle title={t('Action Plan Create')} />
      <Box sx={{ p: 2, minHeight: '100vh' }}>
        <Spinner open={isLoading || loading} />
        <FormContainer formContext={form} onSuccess={handleSubmit}>
          <Stack spacing={2}>
            <Card elevation={4} sx={{ borderRadius: 4 }}>
              <CardContent>
                <TextFieldElement
                  name={`theme`}
                  fullWidth
                  label={t('Action Plan Theme')}
                  required
                  sx={{
                    mb: 3,
                  }}
                />
                <DataGrid
                  rows={failures || []}
                  columns={columns}
                  getRowId={(row) => row.id.toString()}
                  hideFooter
                />
              </CardContent>
            </Card>

            {/* General Comment */}
            <Card elevation={4} sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {t('General Comment')}
                </Typography>
                <TextFieldElement
                  name="comment_general"
                  fullWidth
                  multiline
                  rows={3}
                />
              </CardContent>
            </Card>

            <Card elevation={4} sx={{ borderRadius: 4 }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={1}
                >
                  <Typography fontWeight={18} variant="subtitle1">
                    {t('Create Actions')}
                  </Typography>
                  {!showAddForm && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddCause}
                      color="secondary"
                    >
                      {t('Add Action')}
                    </Button>
                  )}
                </Box>

                {showAddForm && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <TextField
                        name="newAction.description"
                        label={t('Action Description')}
                        fullWidth
                        value={newAction.description}
                        onChange={(e) =>
                          setNewAction((a: any) => ({
                            ...a,
                            description: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        value={newAction.status}
                        onChange={(_, newValue) => {
                          setNewAction((a: any) => ({
                            ...a,
                            status: newValue,
                            // Limpa o implemented se não atender às condições
                            implemented: (() => {
                              const needsImplemented =
                                newValue?.id === 3 &&
                                a.functional_area?.id &&
                                area_leader?.includes(a.functional_area.id);
                              return needsImplemented
                                ? a.implemented
                                : undefined;
                            })(),
                          }));
                        }}
                        options={[
                          { label: t('On Going'), id: 2 },
                          { label: t('Completed'), id: 3 },
                        ]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="newAction.status"
                            label={t('Status')}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <DatePicker
                        name="newAction.due_date"
                        label={t('Due Date')}
                        value={newAction.due_date}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                        onChange={(value) =>
                          setNewAction((a: any) => ({
                            ...a,
                            due_date: value ? dayjs(value) : null,
                          }))
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        value={newAction.functional_area}
                        onChange={(_, newValue) =>
                          setNewAction((a: any) => ({
                            ...a,
                            functional_area: newValue,
                            // Limpa o implemented se não atender às condições
                            implemented: (() => {
                              const needsImplemented =
                                a.status?.id === 3 &&
                                area_leader?.includes(newValue?.id);
                              return needsImplemented
                                ? a.implemented
                                : undefined;
                            })(),
                          }))
                        }
                        options={functionalAreas}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="newAction.functional_area"
                            label={t('Functional Area')}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        name="newAction.responsible"
                        label={t('Responsible')}
                        fullWidth
                        value={newAction.responsible}
                        onChange={(e) =>
                          setNewAction((a: any) => ({
                            ...a,
                            responsible: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                    {newAction.status?.id === 3 &&
                      newAction.functional_area?.id &&
                      area_leader?.includes(newAction.functional_area.id) && (
                        <Grid size={{ xs: 12, md: 2 }}>
                          <Autocomplete
                            value={newAction.implemented}
                            onChange={(_, newValue) =>
                              setNewAction((a: any) => ({
                                ...a,
                                implemented: newValue,
                              }))
                            }
                            options={[
                              { label: t('Yes'), id: 'true' },
                              { label: t('No'), id: 'false' },
                            ]}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="newAction.implemented"
                                label={t('Implemented?')}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      )}
                    <Grid
                      size={{ xs: 12, md: 2 }}
                      display={'flex'}
                      alignItems="center"
                    >
                      <FilePicker
                        label={t('Evidencies')}
                        value={newAction.file}
                        onChange={(file) =>
                          setNewAction((a: any) => ({
                            ...a,
                            file: file,
                          }))
                        }
                        sx={{ height: '100%', fontSize: '18px' }}
                        color="primary"
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 2 }}
                      display={'flex'}
                      alignItems="center"
                    >
                      <Button
                        fullWidth
                        sx={{ height: '100%', fontSize: '18px' }}
                        onClick={handleSaveNewAction}
                        variant="contained"
                        color="secondary"
                        disabled={!isFormValid()}
                      >
                        {editIndex !== null ? t('Save Edit') : t('Save Action')}
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {fields.filter((i) => i.displayed == true).length > 0 && (
                  <Box mt={4}>
                    <DataGrid
                      rows={actions
                        ?.filter((i) => i.displayed == true)
                        .map((field, idx) => ({
                          ...field,
                          id: idx, // DataGrid precisa de um id único
                          index: idx,
                          statusLabel: field.status?.label || '',
                          due_date: formatDateSafely(field.due_date) || '',
                          implementedLabel:
                            typeof field.implemented === 'object'
                              ? field.implemented?.label
                              : field.implemented === true
                                ? t('Yes')
                                : field.implemented === false
                                  ? t('No')
                                  : '',
                        }))}
                      columns={[
                        {
                          field: 'description',
                          headerName: t('Action Description'),
                          flex: 2,
                        },
                        {
                          field: 'functional_area',
                          headerName: t('Functional Area'),
                          flex: 1,
                          renderCell: (params: GridRenderCellParams) => {
                            return (
                              <Box display={'flex'} alignItems="center" gap={1}>
                                {params.row.functional_area?.label || ''}
                              </Box>
                            );
                          },
                        },
                        {
                          field: 'responsible',
                          headerName: t('Responsible'),
                          flex: 1,
                        },
                        {
                          field: 'due_date',
                          headerName: t('Due Date'),
                          flex: 1,
                          renderCell: (params: GridRenderCellParams) => {
                            const value = params.row.due_date;
                            let formatted = '';

                            if (value) {
                              // Se já é uma string formatada (YYYY-MM-DD), converte para DD/MM/YYYY
                              if (
                                typeof value === 'string' &&
                                /^\d{4}-\d{2}-\d{2}$/.test(value)
                              ) {
                                const date = dayjs(value);
                                formatted = date.isValid()
                                  ? date.format('DD/MM/YYYY')
                                  : '';
                              } else {
                                // Para outros formatos, usa dayjs
                                const date = dayjs(value);
                                formatted = date.isValid()
                                  ? date.format('DD/MM/YYYY')
                                  : '';
                              }
                            }

                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{formatted}</span>
                                {formatted && (
                                  <CalendarTodayIcon
                                    color="primary"
                                    fontSize="small"
                                  />
                                )}
                              </Box>
                            );
                          },
                        },
                        {
                          field: 'statusLabel',
                          headerName: t('Status'),
                          flex: 1,
                          renderCell: (params: GridRenderCellParams) => {
                            return (
                              <Chip
                                label={params.row.statusLabel}
                                color={
                                  params.row.statusLabel === t('Completed')
                                    ? 'success'
                                    : params.row.statusLabel === t('On Going')
                                      ? 'warning'
                                      : 'default'
                                }
                                sx={{
                                  backgroundColor: (theme) =>
                                    params.row.statusLabel === t('Completed')
                                      ? alpha(theme.palette.success.main, 0.2)
                                      : params.row.statusLabel === t('On Going')
                                        ? alpha(theme.palette.warning.main, 0.2)
                                        : alpha(theme.palette.grey[400], 0.2),
                                  color: (theme) =>
                                    params.row.statusLabel === t('Completed')
                                      ? theme.palette.success.main
                                      : params.row.statusLabel === t('On Going')
                                        ? theme.palette.warning.main
                                        : theme.palette.text.primary,
                                }}
                              />
                            );
                          },
                        },

                        ...(actions?.some(
                          (action) =>
                            action.status?.id === 3 &&
                            action.functional_area?.id &&
                            area_leader?.includes(action.functional_area.id),
                        )
                          ? [
                              {
                                field: 'implementedLabel',
                                headerName: t('Implemented?'),
                                flex: 1,
                                renderCell: (params: GridRenderCellParams) => {
                                  // Só mostra se o status for "Completed" (id: 3) E se functional_area.id estiver no area_leader
                                  if (
                                    params.row.status?.id !== 3 ||
                                    !params.row.functional_area?.id ||
                                    !area_leader?.includes(
                                      params.row.functional_area.id,
                                    )
                                  ) {
                                    return null;
                                  }
                                  return (
                                    <Chip
                                      label={params.row.implementedLabel}
                                      color={
                                        params.row.implementedLabel === t('Yes')
                                          ? 'success'
                                          : params.row.implementedLabel ===
                                              t('No')
                                            ? 'error'
                                            : 'default'
                                      }
                                      sx={{
                                        backgroundColor: (theme) =>
                                          params.row.implementedLabel ===
                                          t('Yes')
                                            ? alpha(
                                                theme.palette.success.main,
                                                0.2,
                                              )
                                            : params.row.implementedLabel ===
                                                t('No')
                                              ? alpha(
                                                  theme.palette.error.main,
                                                  0.2,
                                                )
                                              : alpha(
                                                  theme.palette.grey[400],
                                                  0.2,
                                                ),
                                        color: (theme) =>
                                          params.row.implementedLabel ===
                                          t('Yes')
                                            ? theme.palette.success.main
                                            : params.row.implementedLabel ===
                                                t('No')
                                              ? theme.palette.error.main
                                              : theme.palette.text.primary,
                                      }}
                                    />
                                  );
                                },
                              },
                            ]
                          : []),

                        {
                          field: 'file',
                          headerName: t('Evidency'),
                          width: 90,
                          renderCell: (params: GridRenderCellParams) => {
                            const file = params.row.file;
                            const isPath =
                              typeof file === 'string' &&
                              file &&
                              !file.startsWith('data:');
                            const isBase64 =
                              typeof file === 'string' &&
                              file &&
                              file.startsWith('data:');
                            return (
                              <Box
                                display={'flex'}
                                alignItems="center"
                                justifyContent={'center'}
                                sx={{ height: '100%' }}
                              >
                                {isPath && (
                                  <Tooltip title={file} placement="top">
                                    <GridActionsCellItem
                                      icon={<DownloadIcon color="primary" />}
                                      label="download"
                                      onClick={() => downloadFileFromPath(file)}
                                    />
                                  </Tooltip>
                                )}
                                {isBase64 && (
                                  <Tooltip
                                    title={t('File not uploaded yet')}
                                    placement="top"
                                  >
                                    <UploadFileIcon color="primary" />
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          },
                        },
                        {
                          field: 'actions',
                          headerName: '',
                          width: 90,
                          renderCell: (params) => (
                            <Box
                              display="flex"
                              gap={1}
                              sx={{ height: '100%' }}
                              alignItems={'center'}
                              justifyContent={'center'}
                            >
                              <GridActionsCellItem
                                icon={<DeleteOutlineIcon />}
                                label="remove"
                                onClick={() => remove(params.row.index)}
                                sx={{
                                  background: '#2D354A',
                                  color: '#0093ff',
                                  borderRadius: '16px',
                                }}
                              />
                              <GridActionsCellItem
                                icon={<EditIcon />}
                                label="edit"
                                onClick={() => {
                                  setEditIndex(params.row.index);
                                  const fieldData = fields[params.row.index];
                                  setNewAction({
                                    ...fieldData,
                                    due_date: fieldData.due_date
                                      ? dayjs(fieldData.due_date)
                                      : null,
                                  });
                                  setShowAddForm(true);
                                }}
                                sx={{
                                  background: '#2D354A',
                                  color: '#ff9800',
                                  borderRadius: '16px',
                                }}
                              />
                            </Box>
                          ),
                        },
                      ]}
                      hideFooter
                    />
                  </Box>
                )}
              </CardContent>{' '}
            </Card>

            <Box display="flex" justifyContent="flex-end" mt={2} gap={3}>
              <Button
                color={'error'}
                variant="outlined"
                onClick={() => navigate('/nnc/actionplan')}
                sx={{ border: '1px solid #0093FF !important', color: '#000' }}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={showAddForm}
              >
                {t('Confirm')}
              </Button>
            </Box>
          </Stack>
        </FormContainer>
      </Box>
    </>
  );
};

export default NNCAnalysisForm;
